import { subscribe } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';

/** Option name handles that represent SSD/storage (so we can replace with upsell size). */
const SSD_OPTION_HANDLES = new Set(['tallennustila', 'ssd', 'storage', 'speicher', 'lagring']);

export default class TotalPriceSection extends window.HTMLElement {
  variantChanged: boolean = false;
  stickyTotalPrice: HTMLElement | null = null;
  totalPriceBox: HTMLElement | null = null;
  footer: HTMLElement | null = null;
  extendedWarrantyInput: HTMLInputElement | null = null;
  ssdUpsellInputs: NodeListOf<HTMLInputElement> | null = null;
  wlanInput: HTMLInputElement | null = null;
  bluetoothInput: HTMLInputElement | null = null;
  onVariantChangeUnsubscribe: (() => void) | null = null;
  private boundWarrantyHandler: (() => void) | null = null;
  private boundSsdHandler: (() => void) | null = null;
  private boundAdapterHandler: (() => void) | null = null;

  constructor() {
    super();

    this.refreshDomRefs();
    this.footer = document.querySelector('footer');
    if (this.stickyTotalPrice && this.stickyTotalPrice.offsetHeight) {
      this.footer?.setAttribute('style', `margin-bottom: ${this.stickyTotalPrice.offsetHeight + 37}px`);
    } else {
      this.footer?.classList.add('mb-[165px]');
      this.footer?.classList.add('m:mb-[133px]');
    }

    window?.addEventListener('scroll', this.scrollListener as EventListener);

    // Wire up warranty text toggle if extended warranty checkbox exists
    this.rebindWarrantyInput();
    this.rebindSsdInputs();
    this.rebindAdapterInputs();

    // Update prices when variant changes (DOM swapped)
    document.addEventListener(
      'product-info:loaded',
      () => {
        this.refreshDomRefs();
        this.updateDisplayedPrices();
        this.updateSsdDisplay();
      },
      { once: true },
    );
    document.addEventListener('product-info:swapped', () => {
      this.refreshDomRefs();
      this.rebindWarrantyInput();
      this.rebindSsdInputs();
      this.rebindAdapterInputs();
      this.updateDisplayedPrices();
      this.updateSsdDisplay();
    });

    this.onVariantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
      this.variantChanged = true;
      this.refreshDomRefs();
      this.rebindWarrantyInput();
      this.rebindSsdInputs();
      this.rebindAdapterInputs();
      this.updateDisplayedPrices();
      this.updateSsdDisplay();
    });
  }

  disconnectedCallback(): void {
    this.onVariantChangeUnsubscribe?.();
    if (this.extendedWarrantyInput && this.boundWarrantyHandler) {
      this.extendedWarrantyInput.removeEventListener('change', this.boundWarrantyHandler);
    }
    if (this.ssdUpsellInputs && this.boundSsdHandler) {
      this.ssdUpsellInputs.forEach((input) => input.removeEventListener('change', this.boundSsdHandler));
    }
    if (this.wlanInput && this.boundAdapterHandler) {
      this.wlanInput.removeEventListener('change', this.boundAdapterHandler);
    }
    if (this.bluetoothInput && this.boundAdapterHandler) {
      this.bluetoothInput.removeEventListener('change', this.boundAdapterHandler);
    }
  }

  scrollListener = (_el: Event) => {
    if (!this.totalPriceBox) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      const boxTop = this.totalPriceBox.offsetTop;
      const boxBottom = boxTop + this.totalPriceBox.offsetHeight;
      const viewportHeight = window.innerHeight;

      const passedInitialScroll = scrollY > 250;
      const originalBoxIsVisible = boxTop < scrollY + viewportHeight && boxBottom > scrollY;

      if (passedInitialScroll && !originalBoxIsVisible) {
        this.show();
      } else {
        this.hide();
      }
    } else {
      if (scrollY > this.totalPriceBox.offsetTop + this.totalPriceBox.offsetHeight) {
        this.show();
      } else if (scrollY < this.totalPriceBox.offsetTop + this.totalPriceBox.offsetHeight) {
        this.hide();
      }
    }
  };

  show() {
    this.stickyTotalPrice?.classList.remove('translate-y-full');
  }

  hide() {
    // If price is updated by chaning variants
    // the price will stay sticky NUV-500
    if (this.variantChanged) return;
    this.stickyTotalPrice?.classList.add('translate-y-full');
  }

  private parseCents(value: string | null | undefined): number {
    if (!value) return 0;
    const num = parseInt(value, 10);
    return Number.isFinite(num) ? num : 0;
  }

  private updateDisplayedPrices(): void {
    const localPriceAreas = Array.from(this.querySelectorAll('[data-price-area]')) as HTMLElement[];
    const globalPriceAreas = Array.from(document.querySelectorAll('[data-price-area]')) as HTMLElement[];
    const priceAreas = Array.from(new Set<HTMLElement>([...localPriceAreas, ...globalPriceAreas]));
    if (!priceAreas.length) return;

    const warrantyChecked = !!this.extendedWarrantyInput?.checked;
    const warrantyCents = warrantyChecked ? this.parseCents(this.extendedWarrantyInput?.dataset.priceCents) : 0;
    const defaults = Array.from(document.querySelectorAll<HTMLElement>('[data-warranty-default]'));
    const extended = Array.from(document.querySelectorAll<HTMLElement>('[data-warranty-extended]'));
    defaults.forEach((el) => el.classList.toggle('hidden', !!warrantyChecked));
    extended.forEach((el) => el.classList.toggle('hidden', !warrantyChecked));

    const ssdChecked = document.querySelector('input[name="ssd-storage-size"]:checked') as HTMLInputElement | null;
    const ssdCents = ssdChecked?.dataset.variantId ? this.parseCents(ssdChecked.dataset.priceCents) : 0;

    const wlanCents = this.wlanInput?.checked ? this.parseCents(this.wlanInput.dataset.priceCents) : 0;
    const bluetoothCents = this.bluetoothInput?.checked ? this.parseCents(this.bluetoothInput.dataset.priceCents) : 0;

    priceAreas.forEach((area) => {
      const baseCents = this.parseCents(area.dataset.basePriceCents);
      const priceValueEl = area.querySelector('[data-price-value]') as HTMLElement | null;
      if (!priceValueEl) return;

      const totalCents = Math.max(baseCents + warrantyCents + ssdCents + wlanCents + bluetoothCents, 0);
      priceValueEl.dataset.priceCents = String(totalCents);
      priceValueEl.textContent = window?.Shopify?.formatMoney?.(totalCents);
    });

    if (this.variantChanged) {
      setTimeout(() => this.show(), 300);
    }
  }

  private rebindWarrantyInput(): void {
    const current = document.querySelector('#ExtendedWarranty') as HTMLInputElement | null;
    if (current === this.extendedWarrantyInput && current) {
      return;
    }

    if (this.extendedWarrantyInput && this.boundWarrantyHandler) {
      this.extendedWarrantyInput.removeEventListener('change', this.boundWarrantyHandler);
    }

    this.extendedWarrantyInput = current;
    if (this.extendedWarrantyInput) {
      this.boundWarrantyHandler = this.updateWarrantyUI.bind(this);
      this.extendedWarrantyInput.addEventListener('change', this.boundWarrantyHandler);
      this.updateWarrantyUI();
    }
  }

  private rebindSsdInputs(): void {
    const current = document.querySelectorAll('input[name="ssd-storage-size"]') as NodeListOf<HTMLInputElement>;

    if (this.ssdUpsellInputs && this.boundSsdHandler) {
      this.ssdUpsellInputs.forEach((input) => input.removeEventListener('change', this.boundSsdHandler));
    }

    this.ssdUpsellInputs = current;
    if (this.ssdUpsellInputs?.length) {
      this.boundSsdHandler = this.handleSsdChange.bind(this);
      this.ssdUpsellInputs.forEach((input) => input.addEventListener('change', this.boundSsdHandler));
      this.updateDisplayedPrices();
      this.updateSsdDisplay();
    } else {
      this.updateSsdDisplay();
    }
  }

  private handleSsdChange(evt: Event): void {
    const changed = evt.target as HTMLInputElement;
    if (changed.checked && this.ssdUpsellInputs) {
      this.ssdUpsellInputs.forEach((input) => {
        if (input !== changed) {
          input.checked = false;
          input.setAttribute('aria-checked', 'false');
        }
      });
      changed.setAttribute('aria-checked', 'true');
    } else {
      changed.setAttribute('aria-checked', 'false');
    }
    this.updateDisplayedPrices();
    this.updateSsdDisplay();
  }

  /**
   * Updates the SSD/storage option value in the total-price summary to show
   * the selected upsell SSD size (replacing the main variant's value), or
   * restores the variant value when no upsell is selected.
   */
  private updateSsdDisplay(): void {
    const ssdChecked = document.querySelector('input[name="ssd-storage-size"]:checked') as HTMLInputElement | null;
    const displayValue = ssdChecked?.value ?? null;

    const optionValueEls = this.querySelectorAll<HTMLElement>('[data-option-name][data-option-value]');
    optionValueEls.forEach((el) => {
      const name = el.getAttribute('data-option-name');
      if (!name || !SSD_OPTION_HANDLES.has(name)) return;
      const fallback = el.getAttribute('data-option-value') ?? '';
      el.textContent = displayValue ?? fallback;
    });
  }

  private updateWarrantyUI(): void {
    const defaultEls = document.querySelectorAll('[data-warranty-default]') as NodeListOf<HTMLElement>;
    const extendedEls = document.querySelectorAll('[data-warranty-extended]') as NodeListOf<HTMLElement>;

    const showExtended = !!this.extendedWarrantyInput?.checked;
    defaultEls.forEach((el) => el.classList.toggle('hidden', showExtended));
    extendedEls.forEach((el) => el.classList.toggle('hidden', !showExtended));

    this.updateDisplayedPrices();
  }

  private rebindAdapterInputs(): void {
    if (this.wlanInput && this.boundAdapterHandler) {
      this.wlanInput.removeEventListener('change', this.boundAdapterHandler);
    }
    if (this.bluetoothInput && this.boundAdapterHandler) {
      this.bluetoothInput.removeEventListener('change', this.boundAdapterHandler);
    }

    this.boundAdapterHandler = this.updateDisplayedPrices.bind(this);
    this.wlanInput = document.querySelector('#upsell-wlan') as HTMLInputElement | null;
    this.bluetoothInput = document.querySelector('#upsell-bluetooth') as HTMLInputElement | null;

    this.wlanInput?.addEventListener('change', this.boundAdapterHandler);
    this.bluetoothInput?.addEventListener('change', this.boundAdapterHandler);
  }

  private refreshDomRefs(): void {
    this.stickyTotalPrice = this.querySelector('#sticky-total-price');
    this.totalPriceBox = this.querySelector('#total-price-box');
  }
}

if (!customElements.get('total-price-section')) {
  window.customElements.define('total-price-section', TotalPriceSection);
}
